import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Clock, User, ArrowRight } from 'lucide-react';
import { hbhEmbedUrl } from '../../data/hbhEpisodes';
import HbhAttribution from './HbhAttribution';

/**
 * HbhVideoPlayer - the primary "watch this episode" player.
 *
 * Lazy-loads the YouTube iframe (only mounts on click) so the initial page
 * paint is fast and Core Web Vitals stay healthy. Until the viewer clicks,
 * we render the HBH thumbnail with a play button.
 *
 * ALWAYS shows the "Sponsored by Home Building Hub" attribution above the
 * player. Full-page episodes get a richer footer via HbhAttribution
 * variant="footer".
 */
const HbhVideoPlayer = ({ episode, autoAttribution = true, startSec = 0, className = '' }) => {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(startSec);
  const embedUrl = hbhEmbedUrl(episode);

  const jumpTo = (secs) => {
    setT(secs);
    setPlaying(true);
  };
  // Expose jumpTo via a ref-like attribute so parent can trigger jumps
  // when the user clicks a chapter link outside the player.
  if (typeof window !== 'undefined' && episode?.slug) {
    window.__hbhPlayerJump = window.__hbhPlayerJump || {};
    window.__hbhPlayerJump[episode.slug] = jumpTo;
  }

  if (!episode || !embedUrl) return null;

  return (
    <div className={className} data-testid={`hbh-video-player-${episode.slug}`}>
      {autoAttribution && (
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <HbhAttribution variant="chip" />
          {episode.duration && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#8a8a8f]">
              <Clock className="w-3 h-3" /> {episode.duration}
            </span>
          )}
          {episode.guest && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[#8a8a8f]">
              <User className="w-3 h-3" /> Guest: {episode.guest}
            </span>
          )}
        </div>
      )}

      <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-lg">
        {playing ? (
          <iframe
            src={`${embedUrl}&autoplay=1${t ? `&start=${t}` : ''}`}
            title={`${episode.title} - Home Building Hub`}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play episode ${episode.number}: ${episode.title}`}
            data-testid={`hbh-video-play-${episode.slug}`}
          >
            <img
              src={episode.thumbnail}
              alt={episode.title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-white/95 group-hover:bg-white group-hover:scale-105 transition-all p-5 shadow-2xl">
                <Play className="w-8 h-8 text-[#1d1d1f] fill-[#1d1d1f] ml-0.5" />
              </div>
            </div>
            <div className="absolute bottom-3 right-3">
              <span className="inline-block rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white">
                {episode.duration || 'Watch'}
              </span>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * HbhVideoStrip - contextual embed for other guides / hubs.
 *
 * Compact tile that leads with the play thumbnail + eyebrow "Watch: Home
 * Building Hub" and a soft-link to the full episode page (/watch/{slug}).
 * Used on H&L hub, builders hub, find-a-builder etc where a topically
 * relevant HBH video should feature but shouldn't dominate the page.
 */
export const HbhVideoStrip = ({ episode, eyebrow = 'Watch: Home Building Hub', className = '' }) => {
  if (!episode) return null;
  return (
    <section
      data-testid={`hbh-video-strip-${episode.slug}`}
      className={`my-6 rounded-2xl border border-gray-200 bg-white overflow-hidden ${className}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
        <Link
          to={`/watch/${episode.slug}`}
          className="relative aspect-video md:aspect-auto md:h-full bg-black block group"
          data-testid={`hbh-video-strip-thumb-${episode.slug}`}
        >
          <img
            src={episode.thumbnail}
            alt={episode.title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-white/95 group-hover:scale-105 transition-transform p-3.5 shadow-lg">
              <Play className="w-5 h-5 text-[#1d1d1f] fill-[#1d1d1f] ml-0.5" />
            </div>
          </div>
          {episode.duration && (
            <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {episode.duration}
            </span>
          )}
        </Link>
        <div className="p-4 sm:p-5 flex flex-col justify-center">
          <div className="mb-2 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#1d8efe]">
              {eyebrow}
            </span>
            <HbhAttribution variant="line" />
          </div>
          <Link
            to={`/watch/${episode.slug}`}
            className="text-[15.5px] sm:text-[17px] font-bold text-[#1d1d1f] hover:text-[#1d8efe] leading-tight tracking-tight transition-colors"
          >
            {episode.title}
          </Link>
          {episode.description && (
            <p className="mt-2 text-[13px] text-[#5a5a5f] leading-[1.55] line-clamp-3">
              {episode.description}
            </p>
          )}
          <Link
            to={`/watch/${episode.slug}`}
            className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#1d1d1f] hover:text-[#1d8efe] transition-colors"
            data-testid={`hbh-video-strip-cta-${episode.slug}`}
          >
            Watch full episode <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HbhVideoPlayer;
